import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import EmailComposer from '../components/EmailComposer';
import EmailBodyFrame from '../components/EmailBodyFrame';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

interface ArchivedTask {
  id: string;
  type: string;
  status: string;
  emailType?: string;
  emailContent?: {
    from?: string;
    to?: string;
    subject?: string;
    body?: string;
    receivedDate?: string;
  };
  assignedClaimId?: string;
  clientName?: string;
  clientEmail?: string;
  subject?: string;
  description?: string;
  archivedAt: string;
  archivedBy: string;
  archiveReason: string;
  replyTracking?: {
    hasBeenRepliedTo: boolean;
    repliedBy: string | null;
    repliedAt: string | null;
    replyCount: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface ArchivedTasksResponse {
  success: boolean;
  tasks: ArchivedTask[];
  pagination: PaginationInfo;
}

const ArchivedTasks: React.FC = () => {
  const [tasks, setTasks] = useState<ArchivedTask[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ArchivedTask | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [replyToTask, setReplyToTask] = useState<ArchivedTask | null>(null);
  const [replyLoadingTaskId, setReplyLoadingTaskId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: string; text: string } | null>(null);
  const [taskContentError, setTaskContentError] = useState<string | null>(null);

  const loadArchivedTasks = useCallback(async (page: number = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get<ArchivedTasksResponse>(`${API_URL}/ui/admin/archived-tasks`, {
        params: {
          page,
          pageSize: pagination.pageSize,
          searchTerm: searchTerm || undefined
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setTasks(response.data.tasks);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error loading archived tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, pagination.pageSize]);

  useEffect(() => {
    loadArchivedTasks(1);
  }, []);

  const handleSearch = () => {
    loadArchivedTasks(1);
  };

  const handlePageChange = (newPage: number) => {
    loadArchivedTasks(newPage);
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const viewTaskDetails = async (task: ArchivedTask) => {
    // Tasks Rebuild Task 14: list rows no longer carry emailContent inline
    // (task_service.get_archived_tasks now calls to_dict(include_content=
    // False) -- see Appendix A/task-14-brief). The detail modal needs the
    // real body/from/to/receivedDate, so fetch it from the dedicated
    // content endpoint (which falls back from email_task_content to
    // archived_email_task_content -- get_content_any -- so this works
    // whether or not the task has since been unarchived).
    setSelectedTask(task);
    setShowTaskModal(true);
    setTaskContentError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get<{ emailContent?: ArchivedTask['emailContent']; claudeAnalysis?: unknown }>(
        `${API_URL}/ui/tasks/${task.id}/content`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.emailContent) {
        setSelectedTask((prev) => (prev && prev.id === task.id ? { ...prev, emailContent: res.data.emailContent } : prev));
      }
    } catch (error) {
      console.error('Error loading archived task content:', error);
      setTaskContentError('Failed to load the full email content for this task. Some details below may be incomplete.');
    }
  };

  const closeTaskModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
    setTaskContentError(null);
  };

  const handleUnarchiveTask = async (taskId: string) => {
    try {
      setUnarchiving(true);
      const token = localStorage.getItem('token');
      const response = await axios.post<{ success: boolean; message: string }>(
        `${API_URL}/ui/admin/archived-tasks/${taskId}/unarchive`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Refresh the tasks list
        await loadArchivedTasks(pagination.page);
        // Close the modal
        closeTaskModal();
        // Optionally show a success message
        alert('Task unarchived successfully');
      }
    } catch (error) {
      console.error('Error unarchiving task:', error);
      alert('Failed to unarchive task. Please try again.');
    } finally {
      setUnarchiving(false);
    }
  };

  const handleReplyToEmail = async (task: ArchivedTask) => {
    // Simplified - no claim fetch needed
    // Email will be sent via Graph API but NOT filed in correspondence
    //
    // Row-level "Reply" (the table action, not the detail modal) can be
    // clicked without ever opening the modal, so `task` may still be the
    // slim list row with no emailContent (see viewTaskDetails above). The
    // composer reads its `defaultTo`/`defaultSubject`/`defaultBody` props
    // only once, at mount (useState(defaultX), no re-sync on prop change),
    // so it MUST NOT be opened until the hydrated task is in hand -- opening
    // it first and hydrating replyToTask afterwards (the old behaviour)
    // leaves the composer permanently blank. Hold the composer closed until
    // the fetch resolves (or the task already carries content, e.g. via the
    // modal path, which pre-hydrates it in viewTaskDetails).
    if (task.emailContent?.from) {
      setReplyToTask(task);
      setShowEmailComposer(true);
      return;
    }

    setReplyLoadingTaskId(task.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get<{ emailContent?: ArchivedTask['emailContent'] }>(
        `${API_URL}/ui/tasks/${task.id}/content`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const hydratedTask = res.data?.emailContent ? { ...task, emailContent: res.data.emailContent } : task;
      setReplyToTask(hydratedTask);
      setShowEmailComposer(true);
    } catch (error) {
      console.error('Error loading archived task content for reply:', error);
      setMessage({ type: 'error', text: 'Failed to load email content. Please try again.' });
      setTimeout(() => setMessage(null), 5000);
    } finally {
      setReplyLoadingTaskId(null);
    }
  };

  const handleEmailSent = () => {
    setShowEmailComposer(false);
    setReplyToTask(null);
    setMessage({ type: 'success', text: 'Email sent successfully' });
    setTimeout(() => setMessage(null), 5000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <span className="text-4xl">📦</span>
                Archived Tasks
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                View and manage archived tasks
              </p>
            </div>

            {/* Search Section */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, email, sender, or subject..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full sm:w-80 px-4 py-2 pl-10 pr-4 text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <button
                onClick={handleSearch}
                className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Search
              </button>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Total Archived</span>
              <span className="text-2xl font-bold text-gray-900">{pagination.total.toLocaleString()}</span>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Currently Showing</span>
              <span className="text-lg font-semibold text-gray-900">
                {pagination.total > 0 ? (
                  `${(pagination.page - 1) * pagination.pageSize + 1} - ${Math.min(pagination.page * pagination.pageSize, pagination.total)}`
                ) : '0'}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading archived tasks...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Table Container */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto relative">
                <table className="min-w-full table-auto">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="w-[35%] px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subject/From
                      </th>
                      <th className="w-[18%] px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Archived Date
                      </th>
                      <th className="w-[18%] px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Archived By
                      </th>
                      <th className="w-[20%] px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Reason
                      </th>
                      <th className="sticky right-0 w-[12%] min-w-[160px] px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tasks.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12">
                          <div className="text-center">
                            <svg
                              className="mx-auto h-12 w-12 text-gray-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                              />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No archived tasks</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              There are no archived tasks matching your search criteria.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      tasks.map((task) => (
                        <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                          <td className="w-[35%] px-6 py-4 align-top">
                            <div className="space-y-1">
                              <div className="text-sm font-medium text-gray-900 break-words" title={task.subject || task.emailContent?.subject || 'No Subject'}>
                                {task.subject || task.emailContent?.subject || 'No Subject'}
                              </div>
                              <div className="text-sm text-gray-500 break-words" title={`From: ${task.emailContent?.from || task.clientEmail || 'Unknown'}`}>
                                From: {task.emailContent?.from || task.clientEmail || 'Unknown'}
                              </div>
                            </div>
                          </td>
                          <td className="w-[18%] px-6 py-4 whitespace-nowrap text-sm text-gray-900 align-top">
                            {formatDateTime(task.archivedAt)}
                          </td>
                          <td className="w-[18%] px-6 py-4 align-top">
                            <div className="text-sm text-gray-900 break-words" title={task.archivedBy}>
                              {task.archivedBy}
                            </div>
                            {task.assignedClaimId && (
                              <div className="text-xs text-gray-500 font-mono mt-1 break-words">
                                Claim: {task.assignedClaimId}
                              </div>
                            )}
                          </td>
                          <td className="w-[20%] px-6 py-4 text-sm text-gray-900 align-top">
                            <p className="break-words" title={task.archiveReason}>
                              {task.archiveReason || 'No reason provided'}
                            </p>
                          </td>
                          <td className="sticky right-0 w-[12%] min-w-[160px] px-6 py-4 bg-white shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.1)] align-top">
                            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                              <button
                                onClick={() => viewTaskDetails(task)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-blue-600 text-xs font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                              >
                                <svg className="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                              </button>
                              <button
                                onClick={() => handleUnarchiveTask(task.id)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-emerald-600 text-xs font-medium rounded-md text-emerald-600 bg-white hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
                              >
                                <svg className="h-3.5 w-3.5 mr-1 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                </svg>
                                Unarchive
                              </button>
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReplyToEmail(task);
                                  }}
                                  disabled={replyLoadingTaskId === task.id}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-purple-600 text-xs font-medium rounded-md text-purple-600 bg-white hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Reply to email"
                                >
                                  {replyLoadingTaskId === task.id ? (
                                    <>
                                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-purple-600 mr-1"></div>
                                      Loading...
                                    </>
                                  ) : (
                                    <>
                                      <svg className="h-3.5 w-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                      </svg>
                                      Reply
                                    </>
                                  )}
                                </button>
                                {task.replyTracking?.hasBeenRepliedTo && (
                                  <div className="text-xs text-green-600 flex items-center gap-1">
                                    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    <span>
                                      Replied by {task.replyTracking.repliedBy?.split('@')[0]}
                                      {task.replyTracking.replyCount > 1 && ` (${task.replyTracking.replyCount}x)`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">{(pagination.page - 1) * pagination.pageSize + 1}</span>
                        {' '}to{' '}
                        <span className="font-medium">{Math.min(pagination.page * pagination.pageSize, pagination.total)}</span>
                        {' '}of{' '}
                        <span className="font-medium">{pagination.total}</span>
                        {' '}results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page >= pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Task Details Modal */}
      {showTaskModal && selectedTask && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeTaskModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-700 to-gray-900 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <span className="text-2xl">📦</span>
                <h2 className="text-xl font-semibold text-white">Archived Task Details</h2>
              </div>
              <button
                onClick={closeTaskModal}
                className="text-white hover:text-gray-300 transition-colors text-2xl font-light leading-none"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 space-y-6">
              {taskContentError && (
                <div className="bg-red-50 border-l-4 border-red-400 rounded-lg p-4 text-sm text-red-800" role="alert">
                  {taskContentError}
                </div>
              )}
              {/* Email Information Section */}
              <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <span className="text-xl">📧</span>
                  Email Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-blue-700">Subject:</label>
                    <p className="text-gray-800 mt-1">{selectedTask.subject || selectedTask.emailContent?.subject || 'No Subject'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-700">From:</label>
                    <p className="text-gray-800 mt-1">{selectedTask.emailContent?.from || selectedTask.clientEmail || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-700">To:</label>
                    <p className="text-gray-800 mt-1">{selectedTask.emailContent?.to || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-blue-700">Received:</label>
                    <p className="text-gray-800 mt-1">{formatDateTime(selectedTask.emailContent?.receivedDate)}</p>
                  </div>
                </div>
              </div>

              {/* Client Information Section */}
              <div className="bg-green-50 border-l-4 border-green-400 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                  <span className="text-xl">👤</span>
                  Client Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-green-700">Name:</label>
                    <p className="text-gray-800 mt-1">{selectedTask.clientName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-green-700">Email:</label>
                    <p className="text-gray-800 mt-1">{selectedTask.clientEmail || 'N/A'}</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-green-700">Assigned Claim ID:</label>
                    <p className="text-gray-800 mt-1 font-mono">{selectedTask.assignedClaimId || 'None'}</p>
                  </div>
                </div>
              </div>

              {/* Archive Information Section */}
              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-amber-900 mb-4 flex items-center gap-2">
                  <span className="text-xl">🗂️</span>
                  Archive Information
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-amber-700">Archived At:</label>
                      <p className="text-gray-800 mt-1">{formatDateTime(selectedTask.archivedAt)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-amber-700">Archived By:</label>
                      <p className="text-gray-800 mt-1">{selectedTask.archivedBy}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-amber-700">Archive Reason:</label>
                    <div className="mt-2 p-3 bg-white rounded-lg border border-amber-200">
                      <p className="text-gray-800 whitespace-pre-wrap">{selectedTask.archiveReason}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Body Section */}
              {selectedTask.emailContent?.body && (
                <div className="bg-gray-50 border-l-4 border-gray-400 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-xl">📄</span>
                    Email Body
                  </h3>
                  {/* Freshdesk #656: sandboxed frame, same as the task detail pane. */}
                  <div className="bg-white rounded-lg border border-gray-200 p-2 h-96 overflow-hidden">
                    <EmailBodyFrame html={selectedTask.emailContent.body} />
                  </div>
                </div>
              )}

              {/* Task Metadata */}
              <div className="bg-purple-50 border-l-4 border-purple-400 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
                  <span className="text-xl">⚙️</span>
                  Task Metadata
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-purple-700">Task ID:</label>
                    <p className="text-gray-800 mt-1 font-mono text-sm">{selectedTask.id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-purple-700">Task Type:</label>
                    <p className="text-gray-800 mt-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {selectedTask.type}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-purple-700">Status:</label>
                    <p className="text-gray-800 mt-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {selectedTask.status}
                      </span>
                    </p>
                  </div>
                  {selectedTask.createdAt && (
                    <div>
                      <label className="text-sm font-medium text-purple-700">Created At:</label>
                      <p className="text-gray-800 mt-1 text-sm">{formatDateTime(selectedTask.createdAt)}</p>
                    </div>
                  )}
                  {selectedTask.updatedAt && (
                    <div>
                      <label className="text-sm font-medium text-purple-700">Last Updated:</label>
                      <p className="text-gray-800 mt-1 text-sm">{formatDateTime(selectedTask.updatedAt)}</p>
                    </div>
                  )}
                  {selectedTask.emailType && (
                    <div>
                      <label className="text-sm font-medium text-purple-700">Email Type:</label>
                      <p className="text-gray-800 mt-1">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {selectedTask.emailType}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer with Actions */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => handleUnarchiveTask(selectedTask.id)}
                disabled={unarchiving}
                className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-base font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {unarchiving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Unarchiving...
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    Unarchive Task
                  </>
                )}
              </button>
              <button
                onClick={() => handleReplyToEmail(selectedTask)}
                disabled={replyLoadingTaskId === selectedTask.id}
                className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-base font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {replyLoadingTaskId === selectedTask.id ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Reply to Email
                  </>
                )}
              </button>
              <button
                onClick={closeTaskModal}
                className="inline-flex items-center justify-center px-6 py-2.5 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Message Display */}
      {message && (
        <div className="fixed top-4 right-4 z-50">
          <div className={`rounded-lg shadow-lg p-4 ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}`}>
            <div className="flex items-center">
              {message.type === 'success' ? (
                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <span className="font-medium">{message.text}</span>
            </div>
          </div>
        </div>
      )}

      {/* Email Composer */}
      {showEmailComposer && replyToTask && (
        <EmailComposer
          claim={undefined}
          taskId={replyToTask.id}
          defaultTo={replyToTask.emailContent?.from || ''}
          defaultSubject={`RE: ${replyToTask.emailContent?.subject || 'Your inquiry'}`}
          defaultBody={`\n\n---\nOriginal Message:\nFrom: ${replyToTask.emailContent?.from}\nDate: ${replyToTask.emailContent?.receivedDate}\nSubject: ${replyToTask.emailContent?.subject}\n\n${replyToTask.emailContent?.body || ''}`}
          onClose={() => {
            setShowEmailComposer(false);
            setReplyToTask(null);
          }}
          onSent={handleEmailSent}
        />
      )}
    </div>
  );
};

export default ArchivedTasks;
